import React, {useEffect, useState} from "react";
import PropTypes from 'prop-types';

import FaceScannerPage from "./FaceScannerPage";

import ScanInstructions from "./ScanInstructions";
import ScanPrepPage from "./ScanPrepPage";
import CompletePage from "../CompletePage";
import RetryFaceScan from "../RetryFaceScan";

import { LoadModels } from "mask-sizing"
import {preCheckBypass} from "./PreCheckBypass";
import {getThresholdConfig} from "./ThresholdConfig";

const NextGenNavigation = ({
                               setFieldValue,
                               handleSubmit,
                               handleComplete,
                               handleImageCapture,
                               currentStep,
                               handleNext,
                               handlePrevious,
                               requestTransition,
                               setBackButtonOverride,
                               setLogoOverride,
                               setExpandedOverride,
                               tokenData,
                               handleDoManual,
                               faceMeshModel,
                               setFaceMeshModel,
                               imageCheckFunction,
                               setImageCheckFunction,
                               nextGenModelsLoading,
                               setNextGenModelsLoading,
                               setHeaderBgColor,
                               thresholdConfig
                           }) => {

    // We save the camera array info and cameraID at the navigation level. So that a
    // user is only prompted once for the camera they want to use if they have multiple cameras.
    //
    // An array of camera dictionary entries in the format of:
    // {
    //   deviceId: ...
    //   label : ...
    //   image: ...
    // }
    const [cameraInfoArray, setCameraInfoArray] = useState([]);

    const [cameraId, setCameraId] = useState(undefined);

    const { disable_precheck, hme_org_code, hme_location_code } = tokenData;
    const [bypassImageCheckFunction, setBypassImageCheckFunction] = useState(imageCheckFunction);

    // If an org has disable_precheck set to true then we disable all pre-checks.
    const loadBypass = async () => {
        await preCheckBypass().then(setBypassImageCheckFunction)
    }

    useEffect( () => {

        if ( disable_precheck ) {
            loadBypass().then().catch(() => {
                console.error("Error loading bypass pre-check")
            })
        }

    }, [disable_precheck]);

    return (
        <>
            {/* ------------------------------------------------------------------------------------------------------------
                Guarantee that the model is loaded. Generally the model will be loaded while the patient is filling out
                the question section, however, we have test code that skips the questions so if that occurs, do the loading
                now.
                ------------------------------------------------------------------------------------------------------------ */}
            {(faceMeshModel === null || imageCheckFunction === null) && nextGenModelsLoading === false && (
              <LoadModels 
                  setFaceMeshModel={setFaceMeshModel}
                  setImageCheckFunction={setImageCheckFunction}
                  setLoadingPromise={setNextGenModelsLoading}
                  thresholdConfig={getThresholdConfig(hme_org_code, hme_location_code)}
              />
            )}
          
            {currentStep === "nextgen_scan_volume" && (
                <ScanInstructions
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    instruction="volume"
                />
            )}
            
            {currentStep === "nextgen_scan_ears" && (
                <ScanInstructions
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    instruction="ears"
                />
            )}
            
            {currentStep === "nextgen_scan_well_lit" && (
                <ScanInstructions
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    instruction="well_lit"
                />
            )}
            
            {currentStep === "nextgen_scan_level" && (
                <ScanInstructions
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    instruction="level"
                />
            )}
            
            {currentStep === "nextgen_scan_final_prep" && (
                <ScanPrepPage
                    handleNext={handleNext}
                    handlePrevious={handlePrevious}
                    setBackButtonOverride={setBackButtonOverride}
                    setLogoOverride={setLogoOverride}
                    setExpandedOverride={setExpandedOverride}
                    setHeaderBgColor={setHeaderBgColor}
                />
            )}
            
            {currentStep === "nextgen_scan_scan_interface" && (
                <FaceScannerPage setFieldValue={setFieldValue}
                                 handleSubmit={handleSubmit}
                                 handleComplete={handleComplete}
                                 handleImageCapture={handleImageCapture}
                                 requestTransition={requestTransition}
                                 faceMeshModel={faceMeshModel}
                                 imageCheckFunction={disable_precheck ? bypassImageCheckFunction : imageCheckFunction}
                                 tokenData={tokenData}
                                 cameraInfoArray={cameraInfoArray}
                                 setCameraInfoArray={setCameraInfoArray}
                                 cameraId={cameraId}
                                 setCameraId={setCameraId}
                                 thresholdConfig={thresholdConfig}

                />
            )}

            {currentStep === "nextgen_scan_scan_retry" && (
              <>
                  <RetryFaceScan
                    errors={{}}
                    onRetry={handleNext}
                    onManual={handleDoManual}
                    setBackButtonOverride={setBackButtonOverride}
                  />
              </>
            )}

            {currentStep === "nextgen_scan_complete" && (
                <CompletePage />
            )}

        </>
    )

}
NextGenNavigation.propTypes = {
    setFieldValue: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleImageCapture: PropTypes.func,
    handleComplete: PropTypes.func,
    currentStep: PropTypes.string.isRequired,
    handleNext: PropTypes.func.isRequired,
    handlePrevious: PropTypes.func.isRequired,
    requestTransition: PropTypes.func.isRequired,
    setBackButtonOverride: PropTypes.func.isRequired,
    setLogoOverride: PropTypes.func.isRequired,
    setExpandedOverride: PropTypes.func.isRequired,
    tokenData: PropTypes.object.isRequired,
    handleDoManual: PropTypes.func.isRequired,
    faceMeshModel: PropTypes.object,
    thresholdConfig: PropTypes.object,
    imageCheckFunction: PropTypes.object,
    setImageCheckFunction: PropTypes.func.isRequired,
    nextGenModelsLoading: PropTypes.bool.isRequired,
    setNextGenModelsLoading: PropTypes.func.isRequired,
    setHeaderBgColor: PropTypes.func.isRequired,
}
export default NextGenNavigation